import { S3Client, ListObjectsV2Command, GetObjectCommand, DeleteObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import type {
  S3Config,
  S3File,
  ListFilesRequest,
  ListFilesResponse,
  DeleteFileRequest,
  DeleteFileResponse,
  GetDownloadUrlRequest,
  GetDownloadUrlResponse,
} from '../types/assetTypes';

export class S3Service {
  private static instance: S3Service | null = null;
  private client: S3Client;
  private bucketName: string;

  private constructor(config: S3Config) {
    this.bucketName = config.bucketName;
    this.client = new S3Client({
      region: config.region || 'auto',
      endpoint: `https://${config.accountId}.r2.cloudflarestorage.com`,
      credentials: {
        accessKeyId: config.accessKeyId,
        secretAccessKey: config.secretAccessKey,
      },
    });
  }

  public static getInstance(): S3Service {
    if (!S3Service.instance) {
      S3Service.instance = new S3Service({
        bucketName: import.meta.env.VITE_MAIN_R2_BUCKET_NAME || '',
        accountId: import.meta.env.VITE_MAIN_R2_ACCOUNT_ID || '',
        accessKeyId: import.meta.env.VITE_MAIN_R2_ACCESS_KEY_ID || '',
        secretAccessKey: import.meta.env.VITE_MAIN_R2_SECRET_ACCESS_KEY || '',
        region: 'auto',
      });
    }
    return S3Service.instance;
  }

  /**
   * List files in the S3 bucket
   */
  async listFiles({ prefix, maxKeys = 1000 }: ListFilesRequest = {}): Promise<ListFilesResponse> {
    try {
      const command = new ListObjectsV2Command({
        Bucket: this.bucketName,
        Prefix: prefix || '',
        MaxKeys: maxKeys,
      });

      const response = await this.client.send(command);

      const files: S3File[] =
        response.Contents?.map((obj) => ({
          key: obj.Key || '',
          name: obj.Key?.split('/').pop() || '',
          size: obj.Size || 0,
          lastModified: obj.LastModified || new Date(),
        })) || [];

      return {
        success: true,
        files,
        count: files.length,
      };
    } catch (error) {
      throw new Error(`Failed to list files: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Upload a file to S3
   */
  // 	async uploadFile({ file, prefix }: UploadFileRequest): Promise<UploadFileResponse> {
  // 		try {
  // 			const key = prefix ? `${prefix}${file.originalname}` : file.originalname;
  //
  // 			const command = new PutObjectCommand({
  // 				Bucket: this.bucketName,
  // 				Key: key,
  // 				Body: file.buffer,
  // 				ContentType: file.mimetype || 'application/octet-stream',
  // 				CacheControl: 'no-cache',
  // 			});
  //
  // 			await this.client.send(command);
  //
  // 			return {
  // 				success: true,
  // 				key,
  // 				message: 'File uploaded successfully',
  // 				size: file.size,
  // 			};
  // 		} catch (error) {
  // 			throw new Error(`Failed to upload file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  // 		}
  // 	}

  /**
   * Delete a file from S3
   */
  async deleteFile({ key }: DeleteFileRequest): Promise<DeleteFileResponse> {
    try {
      const command = new DeleteObjectCommand({
        Bucket: this.bucketName,
        Key: key,
      });

      await this.client.send(command);

      return {
        success: true,
        key,
        message: 'File deleted successfully',
      };
    } catch (error) {
      throw new Error(`Failed to delete file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get a signed download URL for a file
   */
  async getDownloadUrl({ key, expiresIn = 3600 }: GetDownloadUrlRequest): Promise<GetDownloadUrlResponse> {
    try {
      const command = new GetObjectCommand({
        Bucket: this.bucketName,
        Key: key,
      });

      const signedUrl = await getSignedUrl(this.client, command, { expiresIn });
      return {
        success: true,
        downloadUrl: decodeURIComponent(signedUrl),
        key,
        expiresIn,
      };
    } catch (error) {
      throw new Error(`Failed to generate download URL: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get all versions from weave-updates directories
   * @returns Array of unique version numbers found across all platforms
   */
  async getAllVersionsFromWeaveUpdates(): Promise<{ success: boolean; versions: string[]; count: number }> {
    try {
      const directories = ['weave-updates/darwin/universal', 'weave-updates/win32/x64', 'weave-updates/linux/x64'];

      const command = new ListObjectsV2Command({
        Bucket: this.bucketName,
        Prefix: 'weave-updates/',
        MaxKeys: 10000,
      });

      const response = await this.client.send(command);
      const versions = new Set<string>();

      response.Contents?.forEach((file) => {
        if (!file.Key) return;

        // Check if file is in one of our weave-updates directories
        const isInWeaveUpdates = directories.some((dir) => file.Key?.startsWith(dir + '/'));

        if (isInWeaveUpdates) {
          /* Extract version numbers using regex patterns
						Look for patterns like 2.0.85, 1.2.3, etc. */
          const versionMatches = file.Key.match(/(\d+\.\d+\.\d+)/g);

          if (versionMatches) {
            versionMatches.forEach((version) => versions.add(version));
          }
        }
      });

      const sortedVersions = Array.from(versions).sort((a, b) => {
        const aParts = a.split('.').map(Number);
        const bParts = b.split('.').map(Number);

        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aPart = aParts[i] || 0;
          const bPart = bParts[i] || 0;

          if (aPart !== bPart) {
            return bPart - aPart; // Descending order (newest first)
          }
        }
        return 0;
      });

      return {
        success: true,
        versions: sortedVersions,
        count: sortedVersions.length,
      };
    } catch (error) {
      throw new Error(`Failed to get versions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get versions with detailed information by platform
   * @returns Object with versions organized by platform
   */
  async getVersionsByPlatform(): Promise<{
    success: boolean;
    platforms: {
      macos: string[];
      windows: string[];
      linux: string[];
      all: string[];
    };
  }> {
    try {
      const command = new ListObjectsV2Command({
        Bucket: this.bucketName,
        Prefix: 'weave-updates/',
        MaxKeys: 10000,
      });

      const response = await this.client.send(command);
      const platformVersions = {
        macos: new Set<string>(),
        windows: new Set<string>(),
        linux: new Set<string>(),
        all: new Set<string>(),
      };

      response.Contents?.forEach((file) => {
        if (!file.Key) return;

        /* Extract version numbers using regex */
        const versionMatches = file.Key.match(/(\d+\.\d+\.\d+)/g);

        if (versionMatches && file.Key) {
          versionMatches.forEach((version) => {
            platformVersions.all.add(version);

            if (file.Key && file.Key.includes('weave-updates/darwin/universal')) {
              platformVersions.macos.add(version);
            } else if (file.Key && file.Key.includes('weave-updates/win32/x64')) {
              platformVersions.windows.add(version);
            } else if (file.Key && file.Key.includes('weave-updates/linux/x64')) {
              platformVersions.linux.add(version);
            }
          });
        }
      });

      const result = {
        macos: Array.from(platformVersions.macos).sort().reverse(),
        windows: Array.from(platformVersions.windows).sort().reverse(),
        linux: Array.from(platformVersions.linux).sort().reverse(),
        all: Array.from(platformVersions.all).sort().reverse(),
      };

      return {
        success: true,
        platforms: result,
      };
    } catch (error) {
      throw new Error(
        `Failed to get versions by platform: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get asset count for a specific version across all platforms
   * @param version - The version to count assets for (e.g., "2.0.90")
   * @returns Asset count details by platform
   */
  async getVersionAssetCount(version: string): Promise<{
    success: boolean;
    version: string;
    assetCount: number;
    assetDetails: {
      macos: number;
      windows: number;
      linux: number;
    };
  }> {
    try {
      const command = new ListObjectsV2Command({
        Bucket: this.bucketName,
        Prefix: 'weave-updates/',
        MaxKeys: 10000,
      });

      const response = await this.client.send(command);
      const assetCounts = {
        macos: 0,
        windows: 0,
        linux: 0,
      };

      response.Contents?.forEach((file) => {
        if (!file.Key) return;

        // Check if file contains the specific version
        if (file.Key.includes(version)) {
          if (file.Key.includes('weave-updates/darwin/universal')) {
            assetCounts.macos++;
          } else if (file.Key.includes('weave-updates/win32/x64')) {
            assetCounts.windows++;
          } else if (file.Key.includes('weave-updates/linux/x64')) {
            assetCounts.linux++;
          }
        }
      });

      const totalAssetCount = assetCounts.macos + assetCounts.windows + assetCounts.linux;

      return {
        success: true,
        version,
        assetCount: totalAssetCount,
        assetDetails: assetCounts,
      };
    } catch (error) {
      throw new Error(
        `Failed to get asset count for version ${version}: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Generate release table data with version, asset count, and published date
   * @returns Array of ReleaseTableValue objects
   */
  async getReleaseTableData(): Promise<{
    success: boolean;
    releases: Array<{
      version: string;
      assetCount: number;
      published: Date;
      tags: ('darwin' | 'win32' | 'linux')[];
      downloadLinks: {
        linux?: string;
        mac?: string;
        win32?: string;
      };
    }>;
  }> {
    try {
      // First get all unique versions
      const versionsResult = await this.getAllVersionsFromWeaveUpdates();
      if (!versionsResult.success) {
        throw new Error('Failed to get versions');
      }

      const releases = [];

      // For each version, get asset count and published date
      for (const version of versionsResult.versions) {
        const assetCountResult = await this.getVersionAssetCount(version);

        if (assetCountResult.success) {
          // Get the earliest file date for this version as published date
          const command = new ListObjectsV2Command({
            Bucket: this.bucketName,
            Prefix: 'weave-updates/',
            MaxKeys: 10000,
          });

          const response = await this.client.send(command);
          let earliestDate = new Date();
          let foundFile = false;

          response.Contents?.forEach((file) => {
            if (file.Key && file.Key.includes(version) && file.LastModified) {
              if (!foundFile || file.LastModified < earliestDate) {
                earliestDate = file.LastModified;
                foundFile = true;
              }
            }
          });

          // Determine which platforms this version exists on
          const platformTags: ('darwin' | 'win32' | 'linux')[] = [];
          if (assetCountResult.assetDetails.macos > 0) platformTags.push('darwin');
          if (assetCountResult.assetDetails.windows > 0) platformTags.push('win32');
          if (assetCountResult.assetDetails.linux > 0) platformTags.push('linux');

          // Generate download links for each available platform
          const downloadLinks: {
            linux?: string;
            mac?: string;
            win32?: string;
          } = {};

          if (assetCountResult.assetDetails.linux > 0) {
            downloadLinks.linux = `weave-updates/linux/x64/weave_${version}_amd64.deb`;
          }
          if (assetCountResult.assetDetails.macos > 0) {
            downloadLinks.mac = `weave-updates/darwin/universal/Weave-${version}-universal.dmg`;
          }
          if (assetCountResult.assetDetails.windows > 0) {
            downloadLinks.win32 = `weave-updates/win32/x64/Weave-win32-x64-${version}.zip`;
          }

          releases.push({
            version,
            assetCount: assetCountResult.assetCount,
            published: foundFile ? earliestDate : new Date(),
            tags: platformTags,
            downloadLinks,
          });
        }
      }

      // Sort by version (newest first)
      releases.sort((a, b) => {
        const aParts = a.version.split('.').map(Number);
        const bParts = b.version.split('.').map(Number);

        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aPart = aParts[i] || 0;
          const bPart = bParts[i] || 0;

          if (aPart !== bPart) {
            return bPart - aPart; // Descending order (newest first)
          }
        }
        return 0;
      });

      return {
        success: true,
        releases,
      };
    } catch (error) {
      throw new Error(
        `Failed to generate release table data: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get all assets for a specific version organized by platform
   * @param version - The version to get assets for (e.g., "2.0.0")
   * @returns Assets organized by platform
   */
  async getVersionAssets(version: string): Promise<{
    success: boolean;
    version: string;
    assets: {
      darwin: S3File[];
      win32: S3File[];
      linux: S3File[];
    };
  }> {
    try {
      const command = new ListObjectsV2Command({
        Bucket: this.bucketName,
        Prefix: 'weave-updates/',
        MaxKeys: 10000,
      });

      const response = await this.client.send(command);
      const assets = {
        darwin: [] as S3File[],
        win32: [] as S3File[],
        linux: [] as S3File[],
      };

      response.Contents?.forEach((file) => {
        if (!file.Key) return;

        if (file.Key.includes(version)) {
          const fileObj: S3File = {
            key: file.Key,
            name: file.Key.split('/').pop() || '',
            size: file.Size || 0,
            lastModified: file.LastModified || new Date(),
          };

          if (file.Key.includes('weave-updates/darwin/')) {
            assets.darwin.push(fileObj);
          } else if (file.Key.includes('weave-updates/win32/')) {
            assets.win32.push(fileObj);
          } else if (file.Key.includes('weave-updates/linux/')) {
            assets.linux.push(fileObj);
          }
        }
      });

      return {
        success: true,
        version,
        assets,
      };
    } catch (error) {
      throw new Error(`Failed to get version assets: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
